import { useEffect, useState } from "react";
import type { SubscriptionStanding } from "@dentacore/shared-types";

import { readStanding } from "../api/subscription";
import styles from "./subscription-banner.module.css";

/**
 * **A clinic in grace or read-only is told so, on every screen, whatever its
 * role** (STORY-190d, BL-D15). No amount is ever in it — only an admin sees a
 * price, and only in Settings → Modules.
 *
 * A failed read shows nothing: an active clinic, which is nearly all of them,
 * must never be given a banner by a request that went wrong.
 */
export function SubscriptionBanner(): React.JSX.Element | null {
  const [standing, setStanding] = useState<SubscriptionStanding | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    readStanding(controller.signal)
      .then(setStanding)
      .catch(() => {
        if (!controller.signal.aborted) setStanding(null);
      });
    return () => controller.abort();
  }, []);

  if (standing === null || standing.state === "ACTIVE") return null;

  return (
    <div className={styles.banner} role="status" data-testid={`subscription-${standing.state}`}>
      {standing.state === "GRACE" ? (
        <span>
          <strong>Your clinic&rsquo;s subscription needs attention.</strong> Everything still works
          {standing.graceEndsOn === null ? "" : ` until ${standing.graceEndsOn}`}, after which the
          clinic becomes read-only. Please contact DentaCore.
        </span>
      ) : (
        <span>
          <strong>Your clinic is read-only.</strong> Everything can be read and nothing can be
          changed until the subscription is sorted out. Please contact DentaCore.
        </span>
      )}
    </div>
  );
}
